import { useState, useRef, useEffect, type ReactNode } from "react";
import {
  Search,
  Plus,
  Trash2,
  PackagePlus,
  Layers,
  X,
  Check,
} from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { foldSearch } from "@/shared/lib/foldSearch";
import { Button } from "@/shared/ui/core/Button";
import { Input } from "@/shared/ui/core/Input";
import { NumberInput } from "@/shared/ui/core/NumberInput";
import { Badge } from "@/shared/ui/core/Badge";
import { Select } from "@/shared/ui/core/Select";
import { WithTooltip } from "@/shared/ui/core/Tooltip";
import { EntityRef } from "@/shared/ui/patterns/EntityRef";

export interface SearchAppendOption {
  value: string;
  label: string;
  subLabel?: string;
  uom?: string;
  defaultQty?: number;
  spec?: string;
  price?: number;
  [key: string]: any;
}

export interface SearchAppendLineItem {
  id: string;
  itemCode: string;
  itemName: string;
  qty: number;
  uom: string;
  spec?: string;
  price?: number;
  [key: string]: any;
}

export interface SearchAppendRepeaterProps {
  /** Current list of line items */
  items: SearchAppendLineItem[];
  /** Change callback */
  onChange: (items: SearchAppendLineItem[]) => void;
  /** Available search catalog */
  options: SearchAppendOption[];
  /** Search bar placeholder */
  searchPlaceholder?: string;
  /** Section title */
  title?: string;
  /** Step / section indicator (e.g. 3) */
  stepNumber?: number | string;
  /** Required badge */
  required?: boolean;
  /** Default UOM if option doesn't specify */
  defaultUom?: string;
  /** Available UOM choices */
  uomOptions?: Array<{ value: string; label: string }>;
  /** Per-line UOM choices. Overrides `uomOptions` for that row. */
  uomOptionsForLine?: (
    line: SearchAppendLineItem,
  ) => Array<{ value: string; label: string }>;
  /** Allow manual addition of unlisted rows */
  allowManualAdd?: boolean;
  /** Unit name for total summary, e.g. "kg" */
  summaryUnitLabel?: string;
  /** Render without an outer card box/border (ideal for modals). Default false. */
  borderless?: boolean;
  className?: string;
  /** Extra controls after đơn giá (or đơn vị tính), before the delete action. */
  renderLineTrailing?: (
    line: SearchAppendLineItem,
    patch: (partial: Partial<SearchAppendLineItem>) => void,
  ) => ReactNode;
  /** Controls placed before số lượng. */
  renderLineBeforeQty?: (
    line: SearchAppendLineItem,
    patch: (partial: Partial<SearchAppendLineItem>) => void,
  ) => ReactNode;
  /** Header for `renderLineBeforeQty`. */
  beforeQtyHeader?: string;
  /** Override width/class for the before-qty column. */
  beforeQtyColumnClassName?: string;
  /** Override width/class for the số lượng column. */
  qtyColumnClassName?: string;
  /** Override width/class for the tên mặt hàng column. */
  itemColumnClassName?: string;
  /** Đơn vị hiển thị theo dữ liệu dòng, không chọn lại. */
  uomReadOnly?: boolean;
  /** Shared titles above the lines, and a unit-price column. */
  columnHeader?: boolean;
  /** Visible line rows before the list scrolls. */
  maxVisibleLines?: number;
  /** Empty-state title when no lines are selected. */
  emptyTitle?: string;
  /** Empty-state hint under the title. */
  emptyHint?: string;
  /** Tooltip on the remove button. */
  removeLineTitle?: string;
  /** Header of the line-type column when `columnHeader` is set. */
  lineTypeHeader?: string;
  /** Pack column in the shared header. Default true. */
  showPackColumn?: boolean;
  /** Line-type column in the shared header. Default true. */
  showLineTypeColumn?: boolean;
  /** Unit-price column when `columnHeader` is set. Default true. */
  showPriceColumn?: boolean;
  /** UOM column. Default true. */
  showUomColumn?: boolean;
  /** Header for the quantity column. Default "Số lượng". */
  qtyHeader?: string;
  /** Số thập phân ô số lượng. Mặc định 3; dùng 0 cho số tiền. */
  qtyDecimals?: number;
  /** Hậu tố ô số lượng (vd. "đ"). */
  qtySuffix?: string;
  /** Hậu tố theo từng dòng — ghi đè `qtySuffix`. */
  qtySuffixForLine?: (line: SearchAppendLineItem) => string | undefined;
  /** Tiêu đề cột tên dòng. Mặc định "Tên mặt hàng". */
  itemHeader?: string;
  /** `hash` → "#1"; `plain` → "1". */
  indexFormat?: "hash" | "plain";
  /** Class cột STT (header + cell). */
  indexColumnClassName?: string;
  /** Cố định hàng tiêu đề khi cuộn danh sách. */
  stickyHeader?: boolean;
  /** Đồng bộ ô tìm với remote picker (FIN phân bổ đơn…). */
  onSearchChange?: (query: string) => void;
  /** Ẩn nút xóa theo dòng (vd. dòng đơn khóa từ SO/PO). */
  canRemoveLine?: (line: SearchAppendLineItem) => boolean;
  /** Header for `renderLineTrailing`, placed after the standard columns. */
  trailingHeader?: ReactNode;
  /** Ghi đè độ rộng cột trailing (vd. Cần ngày / Ghi chú). */
  trailingColumnClassName?: string;
  /** Ghi đè độ rộng cột nút xóa. */
  actionColumnClassName?: string;
  /** Title of the search result list. */
  catalogTitle?: string;
  /** Message when the search has no match. */
  emptySearchText?: string;
  /** Green code chip under the option title. Off when the title already shows that code. */
  showOptionValue?: boolean;
  /**
   * Replaces the flat catalog list. The search box still opens this panel;
   * `query` is the current text so the caller can search on the server.
   */
  renderResults?: (ctx: {
    query: string;
    append: (option: SearchAppendOption) => void;
    isPicked: (value: string) => boolean;
  }) => ReactNode;
  /** Nội dung cố định phía trên danh sách kết quả khi mở khung tìm. */
  searchPanelHeader?: ReactNode | ((ctx: { close: () => void }) => ReactNode);
  /** Cho phép tạo mới dòng hoặc mặt hàng. Mặc định true. */
  allowCreateNew?: boolean;
  /** Callback tùy chọn khi bấm nút Tạo mới */
  onCreateNew?: (query: string) => void;
  /** Nhãn hiển thị của nút tạo mới, mặc định "Tạo mới" */
  createLabel?: string;
  /** Cùng một mục catalog được thêm lại thành dòng mới, với quy cách để trống. */
  allowRepeatWithDifferentPack?: boolean;
  /** Cảnh báo đỏ trên dòng, ví dụ trùng sản phẩm và quy cách. */
  lineIssue?: (line: SearchAppendLineItem) => string | null;
  /** Đơn vị tiền tệ hiển thị cho cột Đơn giá và Thành tiền (ví dụ: "đ", "VND"). */
  currency?: string;
  /** Click tên/mã mặt hàng trong danh sách đã chọn → peek thông tin cơ bản. */
  onPeekItem?: (itemCode: string) => void;
  /** Tùy biến tiêu đề cột đơn giá. Mặc định "Đơn giá" hoặc "Đơn giá (đ)". */
  priceHeader?: ReactNode;
  /** Cột thành tiền chỉ đọc, đặt trước nút xóa. */
  amountHeader?: string;
  renderLineAmount?: (line: SearchAppendLineItem) => ReactNode;
  /** Footer trong bảng. Khi có, không hiện dải tổng riêng bên dưới. */
  footer?: ReactNode;
  /** Ô tổng số lượng, cùng cột Số lượng. */
  summaryQty?: ReactNode;
  /** Ô tổng tiền dòng, cùng cột Thành tiền. */
  summaryAmount?: ReactNode;
  /** Các dòng tổng bên phải: nhãn rồi giá trị ở cột Thành tiền. */
  summaryLines?: Array<{
    id: string;
    label?: ReactNode;
    value: ReactNode;
    /** Cột Đơn vị, cạnh Đơn giá. */
    unit?: ReactNode;
    /** Cột Đơn giá. Khi có, dòng này thẳng cột với dòng sản phẩm. */
    price?: ReactNode;
    /** Cột ngay sau Đơn giá (hoặc sau `afterPriceOffset` cột extra). */
    afterPrice?: ReactNode;
    /**
     * Số cột `extraColumns` bỏ trống trước `afterPrice` (0 = ngay sau Đơn giá).
     * Ví dụ CK→VAT→Tiền VAT: offset 1 để `afterPrice` nằm dưới cột VAT.
     */
    afterPriceOffset?: number;
    /** Nhãn căn phải, giữa cột sau Đơn giá và Thành tiền. */
    beforeAmount?: ReactNode;
  }>;
  /** Cột thêm sau đơn giá. */
  extraColumns?: Array<{
    id: string;
    header: string;
    cellClassName?: string;
    render: (
      line: SearchAppendLineItem,
      patch: (partial: Partial<SearchAppendLineItem>) => void,
    ) => ReactNode;
  }>;
  /** Cột thêm ngay sau cột Đơn vị. */
  afterUomColumns?: Array<{
    id: string;
    header: string;
    cellClassName?: string;
    render: (
      line: SearchAppendLineItem,
      patch: (partial: Partial<SearchAppendLineItem>) => void,
    ) => ReactNode;
  }>;
  /** Ghi đè độ rộng cột Đơn vị. */
  uomColumnClassName?: string;
  /** Ghi đè độ rộng cột Thành tiền. */
  amountColumnClassName?: string;
  /** Bảng chiếm hết chiều ngang khung, cột theo tỉ lệ. */
  fillWidth?: boolean;
  /** Thu hẹp khoảng cách trong từng dòng. */
  dense?: boolean;
  /**
   * Đưa SL / Đơn giá / cột phụ (VAT, CK) / Thành tiền xuống cuối
   * (sau ĐVT, cột trước SL, pack, trailing). Dùng cho đề nghị mua.
   */
  commercialAtEnd?: boolean;
  /** Ẩn ô tìm/thêm dòng (vd. phiếu thu/chi khóa 1 đơn từ SO/PO). */
  hideSearch?: boolean;
}

function isOtherCatalogKind(value: unknown) {
  const key = typeof value === "string" ? value.trim().toLowerCase() : "";
  return key === "other" || key === "promo";
}

function optionIdentity(option: SearchAppendOption) {
  if (isOtherCatalogKind(option.lineType)) {
    const code =
      typeof option.itemCode === "string" && option.itemCode.trim()
        ? option.itemCode
        : option.value;
    return code.trim().toLowerCase();
  }
  const product =
    typeof option.productCode === "string" && option.productCode
      ? option.productCode
      : option.value;
  return product.trim().toLowerCase();
}

function lineIdentity(line: SearchAppendLineItem) {
  if (isOtherCatalogKind(line.lineType)) {
    return (line.itemCode || "").trim().toLowerCase();
  }
  const product =
    typeof line.productCode === "string" && line.productCode
      ? line.productCode
      : line.itemCode;
  return (product || "").trim().toLowerCase();
}

/** Column widths shared by the line header and each row. */
export const searchAppendLineCol = {
  index: "w-6 shrink-0",
  item: "min-w-0 flex-1",
  qty: "w-24 shrink-0",
  uom: "w-28 shrink-0",
  price: "w-28 shrink-0",
  pack: "w-44 shrink-0",
  lineType: "w-36 shrink-0",
  action: "size-9 shrink-0",
};

const DEFAULT_UOMS = [
  { value: "kg", label: "kg" },
  { value: "tấn", label: "tấn" },
  { value: "thùng", label: "thùng" },
  { value: "hộp", label: "hộp" },
  { value: "bao", label: "bao" },
  { value: "lon", label: "lon" },
];

export function SearchAppendRepeater({
  items,
  onChange,
  options,
  searchPlaceholder = "Gõ mã SKU, tên sản phẩm hoặc barcode để thêm nhanh... (/)",
  title = "Chi tiết dòng hàng / Định mức sản xuất",
  defaultUom = "kg",
  uomOptions = DEFAULT_UOMS,
  uomOptionsForLine,
  allowManualAdd = true,
  summaryUnitLabel = "kg",
  borderless = false,
  className,
  renderLineTrailing,
  renderLineBeforeQty,
  beforeQtyHeader,
  beforeQtyColumnClassName,
  uomReadOnly = false,
  maxVisibleLines,
  emptyTitle = "Chưa có dòng hàng nào được chọn",
  emptyHint = "Gõ tên sản phẩm hoặc mã SKU vào ô tìm kiếm ở trên để tự động thêm dòng vào danh sách.",
  catalogTitle = "Mặt hàng khả dụng",
  emptySearchText = "Không tìm thấy mặt hàng phù hợp",
  removeLineTitle = "Xóa dòng hàng này",
  lineTypeHeader = "Loại dòng",
  showPackColumn = true,
  showLineTypeColumn = true,
  showPriceColumn = true,
  showUomColumn = true,
  qtyHeader = "Số lượng",
  qtyDecimals = 3,
  qtySuffix,
  qtySuffixForLine,
  itemHeader = "Tên mặt hàng",
  indexFormat = "hash",
  indexColumnClassName,
  stickyHeader = false,
  onSearchChange,
  canRemoveLine,
  trailingHeader,
  trailingColumnClassName,
  actionColumnClassName,
  showOptionValue = true,
  renderResults,
  searchPanelHeader,
  allowCreateNew = true,
  onCreateNew,
  createLabel = "Tạo mới",
  allowRepeatWithDifferentPack = false,
  lineIssue,
  extraColumns = [],
  afterUomColumns = [],
  uomColumnClassName,
  qtyColumnClassName,
  itemColumnClassName,
  amountColumnClassName,
  fillWidth = false,
  currency,
  onPeekItem,
  priceHeader,
  amountHeader,
  renderLineAmount,
  footer,
  summaryQty,
  summaryAmount,
  summaryLines = [],
  dense = false,
  hideSearch = false,
  commercialAtEnd = false,
}: SearchAppendRepeaterProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const canCreate = allowCreateNew && (Boolean(onCreateNew) || allowManualAdd);

  // Filter matching options
  const matchingOptions = options.filter((opt) => {
    if (!searchQuery.trim()) return true;
    const query = foldSearch(searchQuery.trim());
    return (
      foldSearch(opt.label).includes(query) ||
      foldSearch(opt.value).includes(query) ||
      (opt.subLabel && foldSearch(opt.subLabel).includes(query)) ||
      (opt.spec && foldSearch(opt.spec).includes(query))
    );
  });

  // Close search popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node)
      ) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Shortcut "/" to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "/" &&
        document.activeElement?.tagName !== "INPUT" &&
        document.activeElement?.tagName !== "TEXTAREA"
      ) {
        e.preventDefault();
        searchInputRef.current?.focus();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Clear highlight timer
  useEffect(() => {
    if (highlightedRowId) {
      const timer = setTimeout(() => setHighlightedRowId(null), 1600);
      return () => clearTimeout(timer);
    }
  }, [highlightedRowId]);

  // Handle append option
  const handleAppendItem = (option: SearchAppendOption) => {
    const existingIndex = items.findIndex(
      (l) => lineIdentity(l) === optionIdentity(option),
    );
    const appendLine = (clearPack: boolean) => {
      const newItemId = String(Date.now() + Math.random());
      const newItem: SearchAppendLineItem = {
        id: newItemId,
        itemCode: clearPack
          ? ""
          : typeof option.itemCode === "string" && option.itemCode
            ? option.itemCode
            : option.value,
        itemName: option.label,
        qty: option.defaultQty != null ? option.defaultQty : 1000,
        uom: clearPack ? "" : option.uom || defaultUom,
        baseUomCode:
          typeof option.baseUomCode === "string" && option.baseUomCode
            ? option.baseUomCode
            : option.uom || defaultUom,
        spec: option.spec || option.subLabel || "",
        price: option.price,
        currency:
          typeof option.currency === "string" && option.currency.trim()
            ? option.currency.trim()
            : undefined,
        packFormat: clearPack
          ? ""
          : typeof option.packFormat === "string"
            ? option.packFormat
            : "",
        productCode:
          typeof option.productCode === "string" ? option.productCode : "",
        itemSku: typeof option.itemSku === "string" ? option.itemSku : "",
        lineType:
          typeof option.lineType === "string" && option.lineType
            ? option.lineType
            : "product",
        packQty:
          typeof option.packQty === "string" ||
          typeof option.packQty === "number"
            ? String(option.packQty)
            : "",
        packUomCode:
          typeof option.packUomCode === "string" ? option.packUomCode : "",
        unitPrice:
          option.price != null
            ? String(option.price)
            : typeof option.unitPrice === "string" ||
                typeof option.unitPrice === "number"
              ? String(option.unitPrice)
              : "",
        vatPercent:
          typeof option.vatPercent === "string" ||
          typeof option.vatPercent === "number"
            ? String(option.vatPercent)
            : "",
        discountAmount:
          typeof option.discountAmount === "string" ||
          typeof option.discountAmount === "number"
            ? String(option.discountAmount)
            : "",
      };
      onChange([...items, newItem]);
      setHighlightedRowId(newItemId);
    };

    if (allowRepeatWithDifferentPack && existingIndex >= 0) {
      const productKey = optionIdentity(option);
      const unfinished = items.find(
        (line) =>
          lineIdentity(line) === productKey &&
          !(typeof line.packFormat === "string" && line.packFormat.trim()),
      );
      if (unfinished) setHighlightedRowId(unfinished.id);
      else appendLine(true);
    } else if (existingIndex >= 0) {
      setHighlightedRowId(items[existingIndex]!.id);
    } else {
      appendLine(false);
    }

    setSearchQuery("");
    setIsSearchOpen(false);
  };

  // Handle manual line addition
  const handleAddManualLine = () => {
    const newItemId = String(Date.now() + Math.random());
    const q = searchQuery.trim();
    const newItem: SearchAppendLineItem = {
      id: newItemId,
      itemCode: q ? `CUSTOM-${items.length + 1}` : `CUSTOM-${items.length + 1}`,
      itemName: q || "Mặt hàng tùy chỉnh mới",
      qty: 1,
      uom: defaultUom,
      spec: "Quy cách theo yêu cầu",
    };
    onChange([...items, newItem]);
    setHighlightedRowId(newItemId);
    setSearchQuery("");
    setIsSearchOpen(false);
  };

  const handleCreateNew = () => {
    const q = searchQuery.trim();
    if (onCreateNew) {
      onCreateNew(q);
      setIsSearchOpen(false);
      setSearchQuery("");
      return;
    }
    if (allowManualAdd) {
      handleAddManualLine();
    }
  };

  // Line modification handlers
  const handleUpdateQty = (id: string, qty: number) => {
    onChange(
      items.map((item) =>
        item.id === id ? { ...item, qty: Math.max(0, qty) } : item,
      ),
    );
  };

  const handleUpdateUom = (id: string, uom: string) => {
    onChange(items.map((item) => (item.id === id ? { ...item, uom } : item)));
  };

  const patchLine = (id: string, partial: Partial<SearchAppendLineItem>) => {
    onChange(
      items.map((item) => (item.id === id ? { ...item, ...partial } : item)),
    );
  };

  const handleRemoveLine = (id: string) => {
    onChange(items.filter((item) => item.id !== id));
  };

  const totalQty = items.reduce((acc, curr) => acc + Number(curr.qty || 0), 0);

  return (
    <div
      className={cn(
        borderless
          ? "space-y-3"
          : "rounded-control border border-border bg-surface p-3.5 space-y-3",
        className,
      )}
    >
      {/* 1. Section Header matching standard form sections */}
      {/* 1. Section Header cleanly separated with divider, no outer border */}
      {title && (
        <div className="flex items-center justify-between border-b border-border/60 pb-2.5 pt-0.5">
          <div className="flex items-center gap-2">
            <span className="flex size-6 items-center justify-center rounded-md bg-primary-soft text-primary shadow-xs">
              <Layers className="size-3.5" />
            </span>
            <h4 className="m-0 text-caption font-bold uppercase tracking-wider text-text">
              {title}
            </h4>
          </div>
          <Badge tone="primary" label={`${items.length} mặt hàng`} />
        </div>
      )}

      {/* 2. Fast Search & Append Bar */}
      {!hideSearch ? (
      <div ref={searchContainerRef} className="relative w-full pt-1.5">
        <div className="relative flex items-center rounded-lg border border-border bg-bg/40 focus-within:border-primary focus-within:bg-surface focus-within:ring-2 focus-within:ring-primary/15 transition-all">
          <Search className="size-4 ml-3 text-text-muted shrink-0 pointer-events-none" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => {
              const next = e.target.value;
              setSearchQuery(next);
              onSearchChange?.(next);
              setIsSearchOpen(true);
            }}
            onClick={() => setIsSearchOpen(true)}
            onFocus={() => {
              setIsSearchOpen(true);
              onSearchChange?.(searchQuery);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setIsSearchOpen(false);
              } else if (
                e.key === "Enter" &&
                isSearchOpen &&
                !renderResults &&
                matchingOptions[0]
              ) {
                e.preventDefault();
                handleAppendItem(matchingOptions[0]);
              } else if (e.key === "ArrowDown" && !isSearchOpen) {
                setIsSearchOpen(true);
              }
            }}
            placeholder={searchPlaceholder}
            className="w-full h-10 bg-transparent pl-2.5 pr-20 text-[13px] text-text placeholder:text-text-muted focus:outline-none"
          />

          <div className="absolute right-2.5 flex items-center gap-1.5">
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setIsSearchOpen(false);
                  searchInputRef.current?.focus();
                }}
                className="p-1 rounded text-text-muted hover:text-text transition-colors cursor-pointer"
                title="Xóa tìm kiếm"
              >
                <X className="size-3.5" />
              </button>
            )}
            <kbd className="hidden sm:inline-flex items-center rounded border border-border/80 bg-surface px-1.5 py-0.5 text-[10px] font-mono text-text-muted select-none">
              / để tìm
            </kbd>
          </div>
        </div>

        {/* Autocomplete Dropdown Popup */}
        {isSearchOpen && (
          <div
            className={cn(
              "absolute left-0 right-0 top-full z-50 mt-1 flex flex-col rounded-lg border border-border bg-surface shadow-xl animate-in fade-in duration-100",
              renderResults ? "max-h-[22rem]" : "max-h-72",
            )}
          >
            {searchPanelHeader ? (
              <div
                className="shrink-0 border-b border-border/70 bg-surface px-1.5 py-1"
                onMouseDown={(e) => e.stopPropagation()}
              >
                {typeof searchPanelHeader === "function"
                  ? searchPanelHeader({
                      close: () => {
                        setIsSearchOpen(false);
                        setSearchQuery("");
                      },
                    })
                  : searchPanelHeader}
              </div>
            ) : null}
            <div className="flex-1 overflow-y-auto p-1.5 scrollbar-thin">
              {renderResults ? (
                renderResults({
                  query: searchQuery,
                  append: handleAppendItem,
                  isPicked: (value) =>
                    items.some((line) => lineIdentity(line) === value),
                })
              ) : matchingOptions.length > 0 ? (
              <div className="space-y-1">
                  <div className="flex items-center justify-between gap-2 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                    <span className="truncate">
                      {catalogTitle} ({matchingOptions.length})
                    </span>
                    <span className="text-[10px] font-normal lowercase text-text-muted hidden sm:inline">
                      Click để thêm vào danh sách
                    </span>
                </div>
                {matchingOptions.map((opt) => {
                    const existingItem = items.find(
                      (l) => lineIdentity(l) === optionIdentity(opt),
                    );
                  const isAlreadyAdded = !!existingItem;
                    const uomCode = (opt.uom || defaultUom).trim();
                    const uomLabel =
                      uomOptions.find(
                        (uom) =>
                          uom.value.trim().toLowerCase() ===
                          uomCode.toLowerCase(),
                      )?.label || uomCode;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAppendItem(opt);
                      }}
                      className={cn(
                        "w-full text-left flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-caption transition-colors cursor-pointer",
                        isAlreadyAdded
                          ? "bg-primary-soft/40 hover:bg-primary-soft/60 border border-primary/25"
                            : "hover:bg-primary-soft/50 focus:bg-primary-soft/50 focus:outline-none",
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-text text-[13px] flex items-center gap-2">
                            <span
                              className={cn(
                                "truncate",
                                isAlreadyAdded && "text-primary font-bold",
                              )}
                            >
                            {opt.label}
                          </span>
                          {isAlreadyAdded && (
                            <span className="shrink-0 text-[10px] text-primary font-bold bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Check className="size-3 text-primary stroke-[3]" />
                                {allowRepeatWithDifferentPack
                                  ? "Thêm quy cách khác"
                                  : "Đã có trong danh sách"}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-text-muted flex items-center gap-2 mt-0.5">
                            {showOptionValue ? (
                          <span
                            className={cn(
                              "shrink-0 font-mono font-medium px-1.5 py-0.2 rounded",
                              isAlreadyAdded
                                ? "bg-primary text-on-primary font-bold"
                                    : "text-primary bg-primary-soft/60",
                            )}
                          >
                            {opt.value}
                          </span>
                            ) : null}

                          {typeof opt.subLabel === "string" &&
                          opt.subLabel.trim() ? (
                            <span className="min-w-0 truncate font-medium tabular-nums">
                              {opt.subLabel.trim()}
                            </span>
                          ) : null}

                          {showOptionValue && opt.defaultQty != null ? (
                              <span className="shrink-0 font-medium">
                                · SL: {opt.defaultQty.toLocaleString("vi-VN")}{" "}
                                {uomLabel}
                              </span>
                          ) : null}
                        </div>
                      </div>

                      <div className="shrink-0">
                        {isAlreadyAdded ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary bg-primary/15 border border-primary/30 px-2.5 py-1 rounded-full">
                            <Check className="size-3.5 text-primary stroke-[2.5]" />
                            Đã thêm
                          </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary bg-primary-soft border border-primary/25 px-2.5 py-1 rounded hover:bg-primary hover:text-on-primary transition-colors cursor-pointer">
                            <Plus className="size-3" />
                            Thêm
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 text-center text-caption text-text-muted">
                  {emptySearchText} {searchQuery ? `"${searchQuery}"` : ""}.
                </div>
              )}
            </div>

            {/* Bottom Create Button - Đồng bộ vị trí với SearchableSelect (Hình 2) */}
            {canCreate && (
              <div className="border-t border-border p-2">
                <Button
                  type="button"
                  variant="soft"
                  size="sm"
                  fullWidth
                  leftIcon={<Plus className="size-4" aria-hidden />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateNew();
                  }}
                >
                  {createLabel}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      ) : null}

      {/* 3. Items Repeater Table / List (Nested Related Table Pattern) */}
      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border/80 bg-bg/40 py-8 text-center">
          <PackagePlus className="size-8 text-text-muted/50 mb-2" />
          <p className="m-0 text-caption font-semibold text-text">
            {emptyTitle}
          </p>
          <p className="m-0 mt-0.5 text-[11px] text-text-muted max-w-sm">
            {emptyHint}
          </p>
        </div>
      ) : (
        <div className="relative w-full overflow-hidden rounded-control border border-border bg-surface shadow-2xs [transform:translateZ(0)]">
          <div
            className={cn(
              "overscroll-x-contain scrollbar-thin",
              "overflow-x-hidden",
            )}
            style={
              maxVisibleLines
                ? {
                    maxHeight: `calc(${maxVisibleLines} * ${dense ? "3.25rem" : "3.75rem"} + ${
                      summaryLines.length > 0 ||
                      summaryQty != null ||
                      summaryAmount != null
                        ? `${(summaryQty != null || summaryAmount != null ? 2.4 : 0) + summaryLines.length * 2.15}rem`
                        : footer
                          ? "10.5rem"
                          : "1.75rem"
                    })`,
                    overflowY: "auto",
                  }
                : undefined
            }
          >
            <table
              className={cn(
                "w-full table-fixed border-separate border-spacing-0 text-left text-body h-55-force-table",
                dense
                  ? "[&_td]:px-1 [&_tbody_td]:py-2.5 [&_th]:px-1 [&_th]:py-1 [&_.rounded-input]:h-[var(--control-height-dense)] [&_.rounded-input]:max-h-[var(--control-height-dense)] [&_.rounded-input]:min-h-[var(--control-height-dense)] [&_.rounded-input]:py-0"
                  : "min-w-full",
              )}
            >
              <style>{`
                .h-55-force-table tbody tr { height: 55px !important; max-height: 55px !important; }
                .h-55-force-table tbody td { height: 55px !important; max-height: 55px !important; padding-top: 0 !important; padding-bottom: 0 !important; box-sizing: border-box !important; }
                .h-55-force-table tbody td > * { max-height: 55px !important; }
              `}</style>
              <thead
                className={cn(
                  "select-none text-text-secondary font-semibold uppercase",
                  dense
                    ? "text-[10px] tracking-normal"
                    : "text-[11px] tracking-wider",
                  stickyHeader &&
                    "[&_th]:sticky [&_th]:top-0 [&_th]:z-[2] [&_th]:bg-bg/95 [&_th]:backdrop-blur-sm",
                )}
              >
                <tr>
                  <th
                    scope="col"
                    style={
                      dense && !fillWidth && !indexColumnClassName
                        ? { width: 40, maxWidth: 40 }
                        : undefined
                    }
                    className={cn(
                      "px-0 py-2.5 text-center bg-bg/80 border-b border-border rounded-tl-[7px] whitespace-nowrap",
                      fillWidth && !indexColumnClassName
                        ? "w-[4%]"
                        : !indexColumnClassName && dense
                          ? "w-10 max-w-10"
                          : !indexColumnClassName
                            ? "w-12"
                            : null,
                      indexColumnClassName,
                    )}
                  >
                    #
                  </th>
                  <th
                    scope="col"
                    className={cn(
                      "px-3 py-2.5 text-left bg-bg/80 border-b border-border",
                      fillWidth
                        ? "w-[21%]"
                        : dense
                          ? "w-auto"
                          : "min-w-44 whitespace-nowrap",
                      itemColumnClassName,
                    )}
                  >
                    {itemHeader}
                  </th>
                  {commercialAtEnd && showUomColumn ? (
                    <th
                      scope="col"
                      className={cn(
                        "px-3 py-2.5 text-left bg-bg/80 border-b border-border",
                        fillWidth
                          ? "w-[6%]"
                          : dense
                            ? "w-[6.75rem]"
                            : "w-28 whitespace-nowrap",
                        uomColumnClassName,
                      )}
                    >
                      Đơn vị
                    </th>
                  ) : null}
                  {commercialAtEnd
                    ? afterUomColumns.map((column) => (
                        <th
                          key={column.id}
                          scope="col"
                          className={cn(
                            "px-3 py-2.5 text-left bg-bg/80 border-b border-border whitespace-nowrap",
                            column.cellClassName,
                          )}
                        >
                          {column.header}
                        </th>
                      ))
                    : null}
                  {beforeQtyHeader || renderLineBeforeQty ? (
                    <th
                      scope="col"
                      className={cn(
                        "px-3 py-2.5 text-left bg-bg/80 border-b border-border",
                        fillWidth
                          ? "w-[14%]"
                          : dense
                            ? "w-[14%]"
                            : "w-44 whitespace-nowrap",
                        beforeQtyColumnClassName,
                      )}
                    >
                      {beforeQtyHeader || "Quy cách"}
                    </th>
                  ) : null}
                  {commercialAtEnd && showPackColumn ? (
                    <th
                      scope="col"
                      className={cn(
                        "px-3 py-2.5 text-left bg-bg/80 border-b border-border",
                        dense ? "w-36" : "w-44 whitespace-nowrap",
                      )}
                    >
                      Quy cách đóng gói
                    </th>
                  ) : null}
                  {commercialAtEnd &&
                  (showLineTypeColumn ||
                    renderLineTrailing ||
                    trailingHeader) ? (
                    <th
                      scope="col"
                      className={cn(
                        "px-3 py-2.5 text-left bg-bg/80 border-b border-border",
                        trailingColumnClassName
                          ? trailingColumnClassName
                          : dense
                            ? "w-[7.25rem]"
                            : "w-36 whitespace-nowrap",
                      )}
                    >
                      {trailingHeader || lineTypeHeader}
                    </th>
                  ) : null}
                  <th
                    scope="col"
                    className={cn(
                      "px-3 py-2.5 text-center bg-bg/80 border-b border-border",
                      fillWidth
                        ? "w-[6%]"
                        : dense
                          ? "w-[5rem]"
                          : "w-28 whitespace-nowrap",
                      qtyColumnClassName,
                    )}
                  >
                    {qtyHeader}
                  </th>
                  {!commercialAtEnd && showUomColumn ? (
                    <th
                      scope="col"
                      className={cn(
                        "px-3 py-2.5 text-left bg-bg/80 border-b border-border",
                        fillWidth
                          ? "w-[6%]"
                          : dense
                            ? "w-[6.75rem]"
                            : "w-28 whitespace-nowrap",
                        uomColumnClassName,
                      )}
                    >
                      Đơn vị
                    </th>
                  ) : null}
                  {!commercialAtEnd
                    ? afterUomColumns.map((column) => (
                        <th
                          key={column.id}
                          scope="col"
                          className={cn(
                            "px-3 py-2.5 text-left bg-bg/80 border-b border-border whitespace-nowrap",
                            column.cellClassName,
                          )}
                        >
                          {column.header}
                        </th>
                      ))
                    : null}
                  {showPriceColumn ? (
                    <th
                      scope="col"
                      className={cn(
                        "px-3 py-2.5 text-right bg-bg/80 border-b border-border",
                        fillWidth
                          ? "w-[9%]"
                          : dense
                            ? "w-[7rem]"
                            : "w-28 whitespace-nowrap",
                      )}
                    >
                      {priceHeader ||
                        (currency ? `Đơn giá (${currency})` : "Đơn giá")}
                    </th>
                  ) : null}
                  {extraColumns.map((column) => (
                    <th
                      key={column.id}
                      scope="col"
                      className={cn(
                        "px-3 py-2.5 text-right bg-bg/80 border-b border-border",
                        fillWidth
                          ? "whitespace-nowrap"
                          : dense
                            ? "w-[5.5rem]"
                            : "w-28 whitespace-nowrap",
                        column.cellClassName,
                      )}
                    >
                      {column.header}
                    </th>
                  ))}
                  {!commercialAtEnd && showPackColumn ? (
                    <th
                      scope="col"
                      className={cn(
                        "px-3 py-2.5 text-left bg-bg/80 border-b border-border",
                        dense ? "w-36" : "w-44 whitespace-nowrap",
                      )}
                    >
                      Quy cách đóng gói
                    </th>
                  ) : null}
                  {!commercialAtEnd &&
                  (showLineTypeColumn ||
                    renderLineTrailing ||
                    trailingHeader) ? (
                    <th
                      scope="col"
                      className={cn(
                        "px-3 py-2.5 text-left bg-bg/80 border-b border-border",
                        trailingColumnClassName
                          ? trailingColumnClassName
                          : dense
                            ? "w-[7.25rem]"
                            : "w-36 whitespace-nowrap",
                      )}
                    >
                      {trailingHeader || lineTypeHeader}
                    </th>
                  ) : null}
                  {renderLineAmount ? (
                    <th
                      scope="col"
                      className={cn(
                        fillWidth ? "w-[9%]" : "w-[9rem]",
                        "px-1 py-2.5 text-right whitespace-nowrap bg-bg/80 border-b border-border",
                        amountColumnClassName,
                      )}
                    >
                      {amountHeader ??
                        (currency ? `Thành tiền (${currency})` : "Thành tiền")}
                    </th>
                  ) : null}
                  <th
                    scope="col"
                    style={
                      dense && !fillWidth && !actionColumnClassName
                        ? { width: 36, maxWidth: 36 }
                        : undefined
                    }
                    className={cn(
                      "py-2.5 text-center bg-bg/80 border-b border-border rounded-tr-[7px] whitespace-nowrap",
                      actionColumnClassName
                        ? cn("px-1", actionColumnClassName)
                        : fillWidth
                          ? "w-[3%] px-1"
                          : dense
                            ? "w-9 max-w-9 px-1"
                            : "w-10 px-1.5 pr-2.5",
                    )}
                  >
                    <span className="sr-only">Thao tác</span>
                  </th>
                </tr>
              </thead>
              <tbody className="text-caption bg-surface">
          {items.map((line, idx) => {
            const isHighlighted = line.id === highlightedRowId;
                  const issue = lineIssue?.(line)?.trim() || null;
                  const isLastRow = idx === items.length - 1;
                  const unitPriceRaw =
                    typeof line.unitPrice === "string" ||
                    typeof line.unitPrice === "number"
                      ? String(line.unitPrice).trim()
                      : "";
                  const unitPriceNumber = Number(unitPriceRaw);
                  const unitPriceValue =
                    unitPriceRaw !== "" && Number.isFinite(unitPriceNumber)
                      ? unitPriceNumber
                      : null;
                  const codeChip =
                    typeof line.productCode === "string" && line.productCode
                      ? line.productCode
                      : showOptionValue
                        ? line.itemCode
                        : "";

                  const cellBorderClass = (isFirst = false, isLast = false) => {
                    if (issue) {
                      return cn(
                        "border-y border-danger bg-danger-soft/20",
                        isFirst && "border-l border-danger rounded-l-lg",
                        isLast && "border-r border-danger rounded-r-lg",
                      );
                    }
                    if (isHighlighted) {
                      return cn(
                        "border-y border-primary bg-primary-soft/50 transition-colors duration-300",
                        isFirst && "border-l border-primary rounded-l-lg",
                        isLast && "border-r border-primary rounded-r-lg",
                      );
                    }
                    return cn(
                      "border-t border-t-transparent",
                      isFirst && "border-l border-l-transparent",
                      isLast && "border-r border-r-transparent",
                      isLastRow
                        ? "border-b border-b-transparent"
                        : "border-b border-border/60",
                      isLastRow && isFirst && "rounded-bl-[7px]",
                      isLastRow && isLast && "rounded-br-[7px]",
                    );
                  };

            return (
                    <tr
                key={line.id}
                      style={{ height: "55px", maxHeight: "55px" }}
                className={cn(
                        "transition-colors [&>td]:py-0 [&>td]:h-[55px] overflow-hidden",
                        !isHighlighted &&
                          !issue &&
                          (idx % 2 === 1
                            ? "bg-bg/20 hover:bg-bg/40"
                            : "hover:bg-bg/40"),
                      )}
                    >
                      {/* STT (First Column) */}
                      <td
                        style={{
                          height: "55px",
                          paddingTop: 0,
                          paddingBottom: 0,
                          ...(dense && !fillWidth && !indexColumnClassName
                            ? { width: 40, maxWidth: 40 }
                            : {}),
                        }}
                        className={cn(
                          cellBorderClass(true, false),
                          fillWidth && !indexColumnClassName
                            ? "w-[4%] px-0 py-0.5"
                            : !indexColumnClassName && dense
                              ? "w-10 max-w-10 px-0 py-0.5"
                              : !indexColumnClassName
                                ? "w-12 px-3 py-0"
                                : "px-0 py-0.5",
                          indexColumnClassName,
                          "text-center font-mono text-caption text-text-muted select-none align-middle whitespace-nowrap",
                        )}
                      >
                        {indexFormat === "plain" ? idx + 1 : `#${idx + 1}`}
                      </td>

                      {/* Tên mặt hàng */}
                      <td
                        style={{
                          height: "55px",
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                        className={cn(
                          cellBorderClass(false, false),
                          fillWidth
                            ? "w-[21%] px-1 py-1.5"
                            : dense
                              ? "w-auto px-1 py-1.5"
                              : "min-w-44 px-3 py-0",
                          itemColumnClassName,
                          "max-w-0 overflow-hidden align-middle",
                        )}
                      >
                        <div
                          style={{ maxHeight: "55px" }}
                          className="flex h-full min-w-0 max-w-full flex-col justify-center gap-0.5 overflow-hidden"
                        >
                          <div
                            className={cn(
                              "flex min-w-0 items-center gap-1.5 overflow-hidden font-medium text-text",
                              dense ? "text-[12px]" : "text-[13px]",
                            )}
                            title={
                              [line.itemName, codeChip].filter(Boolean).join(" · ") ||
                              undefined
                            }
                          >
                            {onPeekItem && line.itemCode ? (
                              <button
                                type="button"
                                className={cn(
                                  "min-w-0 truncate text-left font-mono font-semibold tabular-nums text-primary cursor-pointer no-underline hover:underline hover:text-primary-hover",
                                  dense ? "text-[12px]" : "text-[13px]",
                                )}
                                onClick={() => onPeekItem(line.itemCode)}
                              >
                                {line.itemName}
                              </button>
                            ) : (
                              <span className="min-w-0 truncate">{line.itemName}</span>
                            )}
                            {codeChip ? (
                              <>
                                <span className="shrink-0 text-text-muted" aria-hidden>
                                  ·
                    </span>
                                {onPeekItem && line.itemCode ? (
                                  <EntityRef
                                    label={codeChip}
                                    fullPageTo={`/ops/mdm/items/${encodeURIComponent(line.itemCode)}`}
                                    onPeek={() => onPeekItem(line.itemCode)}
                                    className={cn(
                                      "shrink-0",
                                      dense ? "text-[10px]" : "text-[11px]",
                                    )}
                                  />
                                ) : (
                                  <span
                                    className={cn(
                                      "shrink-0 font-mono font-semibold text-primary",
                                      dense ? "text-[10px]" : "text-[11px]",
                                    )}
                                  >
                                    {codeChip}
                      </span>
                    )}
                              </>
                            ) : null}
                  </div>
                          {line.spec?.trim() ? (
                            <WithTooltip label={line.spec.trim()}>
                              <span
                                className={cn(
                                  "block w-full min-w-0 truncate text-text-muted",
                                  dense ? "text-[10px]" : "text-[11px]",
                                )}
                              >
                                {line.spec.trim()}
                              </span>
                            </WithTooltip>
                          ) : null}
                          {issue ? (
                            <p className="m-0 mt-1 text-[11px] font-medium text-danger-fg">
                              {issue}
                            </p>
                          ) : null}
                </div>
                      </td>

                      {commercialAtEnd && showUomColumn ? (
                        <td
                          style={{
                            height: "55px",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                          className={cn(
                            cellBorderClass(false, false),
                            fillWidth
                              ? "w-[6%] px-1 py-1.5"
                              : dense
                                ? "w-[6.75rem] px-1 py-1.5"
                                : "w-28 px-3 py-0",
                            "align-middle",
                            uomColumnClassName,
                          )}
                        >
                          <div
                            style={{
                              maxHeight: "54px",
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              height: "100%",
                              width: "100%",
                            }}
                          >
                            {uomReadOnly ? (
                  <Input
                    size="sm"
                                readOnly
                                value={
                                  uomOptions.find(
                                    (uom) =>
                                      uom.value.trim().toLowerCase() ===
                                      line.uom.trim().toLowerCase(),
                                  )?.label || line.uom
                                }
                                placeholder="—"
                                className={cn(
                                  "bg-bg/50",
                                  dense ? "px-1.5 text-[12px]" : "h-8.5",
                                )}
                                aria-label={`Đơn vị tính cho ${line.itemName}`}
                              />
                            ) : (
                              <Select
                                size="sm"
                                value={line.uom}
                                onValueChange={(value) =>
                                  handleUpdateUom(line.id, value)
                                }
                                options={(() => {
                                  const choices =
                                    uomOptionsForLine?.(line) ?? uomOptions;
                                  return choices.some(
                                    (uom) => uom.value === line.uom,
                                  )
                                    ? choices
                                    : [
                                        ...choices,
                                        { value: line.uom, label: line.uom },
                                      ];
                                })()}
                                aria-label={`Đơn vị tính cho ${line.itemName}`}
                                rootClassName="w-full"
                              />
                            )}
                          </div>
                        </td>
                      ) : null}

                      {commercialAtEnd
                        ? afterUomColumns.map((column) => (
                            <td
                              key={column.id}
                              className={cn(
                                cellBorderClass(false, false),
                                "px-1 py-1.5 align-middle",
                                column.cellClassName,
                              )}
                            >
                              {column.render(line, (partial) =>
                                patchLine(line.id, partial),
                              )}
                            </td>
                          ))
                        : null}

                      {/* Quy cách / Before Qty */}
                      {beforeQtyHeader || renderLineBeforeQty ? (
                        <td
                          style={{
                            height: "55px",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                          className={cn(
                            cellBorderClass(false, false),
                            fillWidth
                              ? "w-[14%] overflow-hidden px-1 py-0.5"
                              : dense
                                ? "w-[14%] overflow-hidden px-1 py-0.5"
                                : "w-44 px-3 py-0",
                            "align-middle",
                            beforeQtyColumnClassName,
                          )}
                        >
                          {renderLineBeforeQty?.(line, (partial) =>
                            patchLine(line.id, partial),
                          )}
                        </td>
                      ) : null}

                      {commercialAtEnd && showPackColumn ? (
                        <td
                          style={{
                            height: "55px",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                          className={cn(
                            cellBorderClass(false, false),
                            dense ? "w-36 px-1 py-0.5" : "w-44 px-3 py-0",
                            "align-middle",
                          )}
                        />
                      ) : null}

                      {commercialAtEnd &&
                      (showLineTypeColumn ||
                        renderLineTrailing ||
                        trailingHeader) ? (
                        <td
                          style={{
                            height: "55px",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                          className={cn(
                            cellBorderClass(false, false),
                            trailingColumnClassName
                              ? cn(
                                  "overflow-hidden px-1 py-0.5",
                                  trailingColumnClassName,
                                )
                              : dense
                                ? "w-[7.25rem] overflow-hidden px-1 py-0.5"
                                : "w-36 px-3 py-0",
                            "align-middle",
                          )}
                        >
                          <div
                            style={{
                              maxHeight: "54px",
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              height: "100%",
                              width: "100%",
                            }}
                          >
                            {renderLineTrailing?.(line, (partial) =>
                              patchLine(line.id, partial),
                            )}
                          </div>
                        </td>
                      ) : null}

                      {/* Số lượng */}
                      <td
                        style={{
                          height: "55px",
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                        className={cn(
                          cellBorderClass(false, false),
                          fillWidth
                            ? "w-[6%] px-1 py-1.5"
                            : dense
                              ? "w-[5rem] px-1 py-1.5"
                              : "w-28 px-3 py-0",
                          "align-middle",
                          qtyColumnClassName,
                        )}
                      >
                        <div
                          style={{
                            maxHeight: "54px",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                          }}
                        >
                          <NumberInput
                            size="sm"
                            decimals={qtyDecimals}
                            min={0}
                            value={line.qty}
                            suffix={
                              qtySuffixForLine?.(line) ?? qtySuffix
                            }
                            onValueChange={(value) =>
                              handleUpdateQty(
                                line.id,
                                value == null ? 0 : value,
                              )
                            }
                            placeholder={qtyHeader}
                            className={cn(
                              "min-w-0 w-full text-right font-mono font-semibold",
                              dense
                                ? "px-1.5 text-[12px]"
                                : "h-8.5 text-[13px]",
                            )}
                            aria-label={`${qtyHeader} cho ${line.itemName}`}
                          />
                        </div>
                      </td>

                      {/* Đơn vị */}
                      {!commercialAtEnd && showUomColumn ? (
                        <td
                          style={{
                            height: "55px",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                          className={cn(
                            cellBorderClass(false, false),
                            fillWidth
                              ? "w-[6%] px-1 py-1.5"
                              : dense
                                ? "w-[6.75rem] px-1 py-1.5"
                                : "w-28 px-3 py-0",
                            "align-middle",
                            uomColumnClassName,
                          )}
                        >
                          <div
                            style={{
                              maxHeight: "54px",
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              height: "100%",
                              width: "100%",
                            }}
                          >
                            {uomReadOnly ? (
                              <Input
                                size="sm"
                                readOnly
                                value={
                                  uomOptions.find(
                                    (uom) =>
                                      uom.value.trim().toLowerCase() ===
                                      line.uom.trim().toLowerCase(),
                                  )?.label || line.uom
                                }
                                placeholder="—"
                                className={cn(
                                  "bg-bg/50",
                                  dense ? "px-1.5 text-[12px]" : "h-8.5",
                                )}
                                aria-label={`Đơn vị tính cho ${line.itemName}`}
                              />
                            ) : (
                              <Select
                                size="sm"
                    value={line.uom}
                                onValueChange={(value) =>
                                  handleUpdateUom(line.id, value)
                                }
                                options={(() => {
                                  const choices =
                                    uomOptionsForLine?.(line) ?? uomOptions;
                                  return choices.some(
                                    (uom) => uom.value === line.uom,
                                  )
                                    ? choices
                                    : [
                                        ...choices,
                                        { value: line.uom, label: line.uom },
                                      ];
                                })()}
                                aria-label={`Đơn vị tính cho ${line.itemName}`}
                                rootClassName="w-full"
                              />
                            )}
                          </div>
                        </td>
                      ) : null}

                      {!commercialAtEnd
                        ? afterUomColumns.map((column) => (
                            <td
                              key={column.id}
                              className={cn(
                                cellBorderClass(false, false),
                                "px-1 py-1.5 align-middle",
                                column.cellClassName,
                              )}
                            >
                              {column.render(line, (partial) =>
                                patchLine(line.id, partial),
                              )}
                            </td>
                          ))
                        : null}

                      {/* Đơn giá */}
                      {showPriceColumn ? (
                        <td
                          style={{
                            height: "55px",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                          className={cn(
                            cellBorderClass(false, false),
                            fillWidth
                              ? "w-[9%] px-1 py-1.5"
                              : dense
                                ? "w-[7rem] px-1 py-1.5"
                                : "w-28 px-3 py-0",
                            "align-middle",
                          )}
                        >
                          <div className="w-full min-w-0 overflow-hidden">
                            <NumberInput
                              size="sm"
                              decimals={2}
                              min={0}
                              value={unitPriceValue}
                              suffix={currency}
                              onValueChange={(value) =>
                                patchLine(line.id, {
                                  unitPrice: value == null ? "" : String(value),
                                })
                              }
                              placeholder="0"
                              className={cn(
                                "min-w-0 w-full",
                                dense
                                  ? "px-1.5 text-[12px]"
                                  : "h-8.5 text-[13px]",
                              )}
                              aria-label={`Đơn giá cho ${line.itemName}`}
                            />
                          </div>
                        </td>
                      ) : null}

                      {extraColumns.map((column) => (
                        <td
                          key={column.id}
                          style={{
                            height: "55px",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                          className={cn(
                            cellBorderClass(false, false),
                            fillWidth
                              ? "px-1 py-1.5"
                              : dense
                                ? "w-[5.5rem] px-1 py-1.5"
                                : "w-28 px-3 py-0",
                            "align-middle",
                            column.cellClassName,
                          )}
                        >
                          <div className="w-full min-w-0 overflow-hidden">
                            {column.render(line, (partial) =>
                              patchLine(line.id, partial),
                            )}
                          </div>
                        </td>
                      ))}

                      {/* Quy cách đóng gói */}
                      {!commercialAtEnd && showPackColumn ? (
                        <td
                          style={{
                            height: "55px",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                          className={cn(
                            cellBorderClass(false, false),
                            dense ? "w-36 px-1 py-0.5" : "w-44 px-3 py-0",
                            "align-middle",
                          )}
                        />
                      ) : null}

                      {/* Loại sản phẩm / Trailing */}
                      {!commercialAtEnd &&
                      (showLineTypeColumn ||
                        renderLineTrailing ||
                        trailingHeader) ? (
                        <td
                          style={{
                            height: "55px",
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                          className={cn(
                            cellBorderClass(false, false),
                            trailingColumnClassName
                              ? cn("overflow-hidden px-1 py-0.5", trailingColumnClassName)
                              : dense
                                ? "w-[7.25rem] overflow-hidden px-1 py-0.5"
                                : "w-36 px-3 py-0",
                            "align-middle",
                          )}
                        >
                          <div
                            style={{
                              maxHeight: "54px",
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              height: "100%",
                              width: "100%",
                            }}
                          >
                            {renderLineTrailing?.(line, (partial) =>
                              patchLine(line.id, partial),
                            )}
                </div>
                        </td>
                      ) : null}

                      {renderLineAmount ? (
                        <td
                          className={cn(
                            cellBorderClass(false, false),
                            fillWidth ? "w-[9%]" : "w-[9rem]",
                            "px-1 py-1.5 text-right align-middle whitespace-nowrap",
                            amountColumnClassName,
                          )}
                        >
                          {renderLineAmount(line)}
                        </td>
                      ) : null}

                      {/* Thao tác xóa (Last Column) */}
                      <td
                        style={{
                          height: "55px",
                          paddingTop: 0,
                          paddingBottom: 0,
                          ...(dense && !fillWidth && !actionColumnClassName
                            ? { width: 36, maxWidth: 36 }
                            : {}),
                        }}
                        className={cn(
                          cellBorderClass(false, true),
                          actionColumnClassName
                            ? cn("px-1 py-0.5", actionColumnClassName)
                            : fillWidth
                              ? "w-[3%] px-1 py-0.5"
                              : dense
                                ? "w-9 max-w-9 px-1 py-0.5"
                                : "w-10 px-1.5 pr-2.5 py-0",
                          "text-center align-middle whitespace-nowrap",
                        )}
                      >
                        {canRemoveLine && !canRemoveLine(line) ? (
                          <span className="inline-block size-5" aria-hidden />
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleRemoveLine(line.id)}
                            title={removeLineTitle}
                            aria-label={`Xóa ${line.itemName}`}
                            className={cn(
                              "inline-flex items-center justify-center rounded-control border border-transparent text-danger/80 transition-colors hover:border-danger/30 hover:bg-danger-soft hover:text-danger cursor-pointer",
                              actionColumnClassName
                                ? "size-5"
                                : dense
                                  ? "size-5"
                                  : "size-6",
                            )}
                          >
                            <Trash2
                              className={
                                actionColumnClassName
                                  ? "size-3"
                                  : dense
                                    ? "size-3"
                                    : "size-3.5"
                              }
                            />
                          </button>
                        )}
                      </td>
                    </tr>
            );
          })}
              </tbody>
              {footer ||
              summaryQty != null ||
              summaryAmount != null ||
              summaryLines.length > 0 ? (
                <tfoot>
                  {summaryQty != null || summaryAmount != null ? (
                    <tr>
                      {Array.from({
                        length: commercialAtEnd
                          ? 2 +
                            (showUomColumn ? 1 : 0) +
                            afterUomColumns.length +
                            (beforeQtyHeader || renderLineBeforeQty ? 1 : 0) +
                            (showPackColumn ? 1 : 0) +
                            (showLineTypeColumn ||
                            renderLineTrailing ||
                            trailingHeader
                              ? 1
                              : 0)
                          : 2 +
                            (beforeQtyHeader || renderLineBeforeQty ? 1 : 0),
                      }).map((_, index) => (
                        <td
                          key={`sum-before-${index}`}
                          className="border-t border-border bg-[#fbfaf6] px-1 py-0.5 dark:bg-surface-elevated/40"
                        />
                      ))}
                      <td className="border-t border-border bg-[#fbfaf6] px-1 py-0.5 align-middle dark:bg-surface-elevated/40">
                        {summaryQty}
                      </td>
                      {Array.from({
                        length: commercialAtEnd
                          ? (showPriceColumn ? 1 : 0) + extraColumns.length
                          : (showUomColumn ? 1 : 0) +
                            afterUomColumns.length +
                            (showPriceColumn ? 1 : 0) +
                            extraColumns.length +
                            (showPackColumn ? 1 : 0) +
                            (showLineTypeColumn ||
                            renderLineTrailing ||
                            trailingHeader
                              ? 1
                              : 0),
                      }).map((_, index) => (
                        <td
                          key={`sum-mid-${index}`}
                          className="border-t border-border bg-[#fbfaf6] px-1 py-0.5 dark:bg-surface-elevated/40"
                        />
                      ))}
                      {renderLineAmount ? (
                        <td className="border-t border-border bg-[#fbfaf6] px-2 py-0.5 align-middle dark:bg-surface-elevated/40">
                          {summaryAmount}
                        </td>
                      ) : null}
                      <td className="border-t border-border bg-[#fbfaf6] px-0 py-0.5 dark:bg-surface-elevated/40" />
                    </tr>
                  ) : null}
                  {summaryLines.map((row) => {
                    const footCell =
                      "border-t border-border bg-[#fbfaf6] px-1 py-1 align-middle dark:bg-surface-elevated/40";
                    const hasTrailingCol =
                      showLineTypeColumn ||
                      renderLineTrailing ||
                      trailingHeader;
                    const beforeAmountColSpan =
                      2 +
                      (beforeQtyHeader || renderLineBeforeQty ? 1 : 0) +
                      1 +
                      (showUomColumn ? 1 : 0) +
                      afterUomColumns.length +
                      (showPriceColumn ? 1 : 0) +
                      extraColumns.length +
                      (showPackColumn ? 1 : 0) +
                      (hasTrailingCol ? 1 : 0);
                    if (row.price == null) {
                      return (
                        <tr key={row.id}>
                          <td
                            colSpan={beforeAmountColSpan}
                            className="border-t border-border bg-[#fbfaf6] px-2 py-1 text-right align-middle text-caption font-bold text-text dark:bg-surface-elevated/40"
                          >
                            {row.label ?? row.beforeAmount}
                          </td>
                          <td className="border-t border-border bg-[#fbfaf6] px-1 py-1 text-right align-middle dark:bg-surface-elevated/40">
                            {row.value}
                          </td>
                          <td className="border-t border-border bg-[#fbfaf6] px-0 py-1 dark:bg-surface-elevated/40" />
                        </tr>
                      );
                    }
                    const beforePrice =
                      commercialAtEnd
                        ? 2 +
                          (showUomColumn ? 1 : 0) +
                          afterUomColumns.length +
                          (beforeQtyHeader || renderLineBeforeQty ? 1 : 0) +
                          (showPackColumn ? 1 : 0) +
                          (hasTrailingCol ? 1 : 0) +
                          1
                        : 2 +
                          (beforeQtyHeader || renderLineBeforeQty ? 1 : 0) +
                          1;
                    const afterPriceOffset = Math.max(
                      0,
                      Math.min(
                        row.afterPriceOffset ?? 0,
                        extraColumns.length,
                      ),
                    );
                    const afterPriceCells =
                      row.afterPrice != null ? 1 + afterPriceOffset : 0;
                    const betweenPriceAndAmount =
                      Math.max(extraColumns.length - afterPriceCells, 0) +
                      (commercialAtEnd
                        ? 0
                        : (showPackColumn ? 1 : 0) + (hasTrailingCol ? 1 : 0));
                    return (
                      <tr key={row.id}>
                        {Array.from({ length: beforePrice }).map((_, index) => (
                          <td
                            key={`${row.id}-lead-${index}`}
                            className={footCell}
                          />
                        ))}
                        {!commercialAtEnd && showUomColumn ? (
                          <td
                            className={cn(
                              footCell,
                              "text-right text-caption font-bold text-text",
                            )}
                          >
                            {row.unit}
                          </td>
                        ) : null}
                        {!commercialAtEnd
                          ? afterUomColumns.map((column) => (
                              <td
                                key={`${row.id}-${column.id}`}
                                className={footCell}
                              />
                            ))
                          : null}
                        {showPriceColumn ? (
                          <td
                            className={cn(
                              footCell,
                              "text-right text-caption font-bold text-text",
                            )}
                          >
                            {row.price}
                          </td>
                        ) : null}
                        {row.afterPrice != null
                          ? Array.from({ length: afterPriceOffset }).map(
                              (_, index) => (
                                <td
                                  key={`${row.id}-extra-lead-${index}`}
                                  className={footCell}
                                />
                              ),
                            )
                          : null}
                        {row.afterPrice != null ? (
                          <td className={footCell}>{row.afterPrice}</td>
                        ) : null}
                        {betweenPriceAndAmount > 0 ? (
                          <td
                            colSpan={betweenPriceAndAmount}
                            className={cn(
                              footCell,
                              "px-2 text-right text-caption font-bold text-text",
                            )}
                          >
                            {row.beforeAmount}
                          </td>
                        ) : null}
                        {renderLineAmount ? (
                          <td className={cn(footCell, "text-right")}>
                            {row.value}
                          </td>
                        ) : null}
                        <td className={cn(footCell, "px-0")} />
                      </tr>
                    );
                  })}
                  {footer &&
                  summaryLines.length === 0 &&
                  summaryQty == null &&
                  summaryAmount == null ? (
                    <tr>
                      <td
                        colSpan={99}
                        className="border-t border-border bg-[#fbfaf6] px-3 py-2 dark:bg-surface-elevated/40"
                      >
                        {footer}
                      </td>
                    </tr>
                  ) : null}
                </tfoot>
              ) : null}
            </table>
          </div>
        </div>
      )}

      {footer ||
      summaryQty != null ||
      summaryAmount != null ||
      summaryLines.length > 0 ? null : (
      <div className="flex items-center justify-between rounded-lg bg-[#fbfaf6] dark:bg-surface-elevated/40 px-4 py-2.5 text-caption border border-border/60">
        <span className="text-text-muted">
            Tổng cộng{" "}
            <strong className="text-text font-bold">{items.length}</strong> mặt
            hàng
        </span>
        <span className="text-text-muted">
            Tổng số lượng:{" "}
          <strong className="text-primary font-mono text-[14px] font-bold">
            {totalQty.toLocaleString("vi-VN")}
            </strong>
            {summaryUnitLabel ? (
              <span className="font-semibold text-text">
                {" "}
                {summaryUnitLabel}
              </span>
            ) : null}
        </span>
      </div>
      )}
    </div>
  );
}
